import React, { useMemo } from 'react';
import { Col, Row, Space, Tooltip, Typography } from 'antd';
import { AreaLink } from '../AreaLink';
import { MetricLink } from '../MetricLink';
import { resolveBenchmarkRangesForMetric } from '../../helpers/metricRules';
import {
  BENCHMARK_FIELD_LABELS,
  BenchmarkField,
  DEFAULT_BENCHMARK_FIELDS,
  buildBenchmarkMetricCandidateIndex,
  extractBaseMetricKey,
  findBenchmarkMetricCandidates,
  formatBenchmarkValue,
  getBenchmarkFieldValue,
  normalizeBenchmarkField,
  resolveBenchmarkBand,
} from './benchmarkUtils';

interface Props {
  section: any;
  data: any[];
  areas: any[];
  metrics: any[];
  previewAreaId: number;
  dateRange?: [any, any];
  onAreaClick?: (areaId: number) => void;
  onMetricClick?: (metricKey: string, metricId: number | undefined, areaId: number) => void;
}

const DEFAULT_PALETTE = ['#1677ff', '#2f54eb', '#52c41a', '#faad14', '#ff4d4f'];

const BAND_COLORS: Record<string, string> = {
  '1': '#ff4d4f',
  '2': '#fa8c16',
  '3': '#faad14',
  '4': '#52c41a',
  '5': '#389e0d',
};

const DISCRETE_GAUGE_COLORS = ['#ff4d4f', '#fa8c16', '#faad14', '#7cb305', '#52c41a'];
const DISCRETE_GAUGE_LABELS = ['Poor', 'Fair', 'Good', 'Great', 'Perfect'];

const BENCHMARK_DESCRIPTIONS: Record<string, string> = {
  '1': 'Poor: far from target, immediate action recommended.',
  '2': 'Fair: below target, improvement is needed.',
  '3': 'Good: acceptable status, monitor regularly.',
  '4': 'Great: strong condition, maintain current practices.',
  '5': 'Perfect: ideal condition, benchmark reference level.',
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const logBenchmarkCardsDebug = (_payload: any) => {
  // debug logging disabled in production
};

const toRgba = (hexColor: string, alpha: number) => {
  const hex = hexColor.replace('#', '').trim();
  if (hex.length === 3) {
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  if (hex.length === 6) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return `rgba(22, 119, 255, ${alpha})`;
};

const toPolarPoint = (cx: number, cy: number, radius: number, angleDeg: number) => {
  const angleRad = (angleDeg * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(angleRad),
    y: cy - radius * Math.sin(angleRad),
  };
};

const describeArc = (cx: number, cy: number, radius: number, startDeg: number, endDeg: number) => {
  const start = toPolarPoint(cx, cy, radius, startDeg);
  const end = toPolarPoint(cx, cy, radius, endDeg);
  const largeArcFlag = Math.abs(startDeg - endDeg) > 180 ? 1 : 0;
  const sweepFlag = startDeg > endDeg ? 1 : 0;
  return `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} A ${radius} ${radius} 0 ${largeArcFlag} ${sweepFlag} ${end.x.toFixed(2)} ${end.y.toFixed(2)}`;
};

const GaugeDial: React.FC<{
  needleBand?: number | null;
}> = ({ needleBand }) => {
  const width = 280;
  const height = 200;
  const cx = 140;
  const cy = 130;
  const radius = 86;
  const strokeWidth = 28;
  const segmentCount = DISCRETE_GAUGE_COLORS.length;
  const gapDeg = 3;
  const totalGapDeg = gapDeg * (segmentCount - 1);
  const segmentDeg = (180 - totalGapDeg) / segmentCount;
  const safeBand = needleBand && Number.isFinite(needleBand)
    ? Math.max(1, Math.min(segmentCount, Math.round(needleBand)))
    : null;
  const needleAngle = safeBand
    ? 180 - ((safeBand - 1) * (segmentDeg + gapDeg) + segmentDeg / 2)
    : 90;
  const needleTip = toPolarPoint(cx, cy, radius - 24, needleAngle);

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Benchmark gauge">
      {DISCRETE_GAUGE_COLORS.map((color, index) => {
        const startDeg = 180 - index * (segmentDeg + gapDeg);
        const endDeg = startDeg - segmentDeg;
        return (
          <path
            key={`segment-${index}`}
            d={describeArc(cx, cy, radius, startDeg, endDeg)}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="butt"
          />
        );
      })}

      {DISCRETE_GAUGE_COLORS.map((color, index) => {
        const startDeg = 180 - index * (segmentDeg + gapDeg);
        const endDeg = startDeg - segmentDeg;
        const centerDeg = (startDeg + endDeg) / 2;
        const point = toPolarPoint(cx, cy, radius + 38, centerDeg);
        return (
          <text
            key={`band-label-${index}`}
            x={point.x}
            y={point.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={11}
            fontWeight={700}
            fill="#4a4a4a"
          >
            {DISCRETE_GAUGE_LABELS[index]}
          </text>
        );
      })}

      <line
        x1={cx}
        y1={cy}
        x2={needleTip.x}
        y2={needleTip.y}
        stroke="#4a4a4a"
        strokeWidth={3}
        strokeLinecap="round"
      />
    </svg>
  );
};

export const ReportBenchmarkCards: React.FC<Props> = ({
  section,
  data,
  areas,
  metrics,
  previewAreaId,
  dateRange,
  onAreaClick,
  onMetricClick,
}) => {
  const activeFields = useMemo(() => {
    const normalized = (Array.isArray(section?.fields) ? section.fields : [])
      .map((field: string) => normalizeBenchmarkField(String(field)))
      .filter((field: BenchmarkField | null): field is BenchmarkField => field !== null);
    return normalized.length > 0 ? normalized : DEFAULT_BENCHMARK_FIELDS;
  }, [section?.fields]);

  const metricByKey = useMemo(() => {
    const map = new Map<string, any>();
    (Array.isArray(metrics) ? metrics : []).forEach((metric: any) => {
      if (metric?.key) map.set(metric.key, metric);
    });
    return map;
  }, [metrics]);

  const metricCandidateIndex = useMemo(
    () => buildBenchmarkMetricCandidateIndex(Array.isArray(metrics) ? metrics : []),
    [metrics]
  );

  const areaById = useMemo(() => {
    const map = new Map<number, any>();
    (Array.isArray(areas) ? areas : []).forEach((area: any) => {
      if (area?.id) map.set(area.id, area);
    });
    return map;
  }, [areas]);

  const areaNameById = useMemo(() => {
    const map = new Map<number, string>();
    (Array.isArray(areas) ? areas : []).forEach((area: any) => {
      if (area?.id && area?.name) map.set(area.id, area.name);
    });
    return map;
  }, [areas]);

  const rows = Array.isArray(data) ? data : [];
  const normalizedPreviewAreaId = useMemo(() => {
    const parsed = Number(previewAreaId);
    return Number.isFinite(parsed) ? parsed : previewAreaId;
  }, [previewAreaId]);
  const showParent = section?.showParent !== false;
  const selectedAreaName = useMemo(() => {
    const target = (Array.isArray(areas) ? areas : []).find((area: any) => area?.id === previewAreaId);
    return target?.name || null;
  }, [areas, previewAreaId]);
  const dateRangeLabel = useMemo(() => {
    if (!Array.isArray(dateRange) || dateRange.length < 2) return null;
    const [start, end] = dateRange;
    const startLabel = start?.format ? start.format('YYYY-MM-DD') : null;
    const endLabel = end?.format ? end.format('YYYY-MM-DD') : null;
    if (!startLabel || !endLabel) return null;
    return `${startLabel} → ${endLabel}`;
  }, [dateRange]);
  const cardSpan = useMemo(() => {
    const raw = Number(section?.cardSpan);
    if (!Number.isFinite(raw)) return 24;
    return Math.max(1, Math.min(24, Math.round(raw)));
  }, [section?.cardSpan]);
  const cardColumnProps = useMemo(() => {
    if (cardSpan >= 24) return { xs: 24, sm: 24, md: 24, lg: 24 };
    return {
      xs: 24,
      sm: cardSpan <= 12 ? 12 : 24,
      md: cardSpan,
      lg: cardSpan,
    };
  }, [cardSpan]);
  const metricOrder = useMemo(() => {
    const map = new Map<string, number>();
    (Array.isArray(section?.metrics) ? section.metrics : []).forEach((key: string, idx: number) => {
      map.set(key, idx);
    });
    return map;
  }, [section?.metrics]);

  const orderedRows = useMemo(() => {
    type OrderedRow = {
      row: any;
      originalIndex: number;
      metricKey: string;
      metricRank: number;
      areaId: number;
      area: any;
      areaName?: string;
      isParentArea: boolean;
    };

    return rows
      .map((row: any, index: number): OrderedRow => {
        const metricKey = row?.metricKey || section?.metrics?.[0] || `metric-${index}`;
        const rawAreaId = row?.areaId ?? previewAreaId;
        const parsedAreaId = Number(rawAreaId);
        const areaId = Number.isFinite(parsedAreaId) ? parsedAreaId : normalizedPreviewAreaId;
        const area = areaById.get(areaId);
        const areaName = areaNameById.get(areaId);
        const metricRank = metricOrder.has(metricKey) ? (metricOrder.get(metricKey) as number) : Number.MAX_SAFE_INTEGER;
        return {
          row,
          originalIndex: index,
          metricKey,
          metricRank,
          areaId,
          area,
          areaName,
          isParentArea: areaId === normalizedPreviewAreaId,
        };
      })
      .sort((a, b) => {
        if (a.metricRank !== b.metricRank) return a.metricRank - b.metricRank;
        if (a.metricKey !== b.metricKey) return a.metricKey.localeCompare(b.metricKey);
        if (a.isParentArea !== b.isParentArea) return a.isParentArea ? -1 : 1;
        const aArea = a.areaName || '';
        const bArea = b.areaName || '';
        if (aArea !== bArea) return aArea.localeCompare(bArea);
        return a.originalIndex - b.originalIndex;
      });
  }, [rows, section?.metrics, normalizedPreviewAreaId, areaById, areaNameById, metricOrder]);

  const visibleRows = useMemo(
    () => orderedRows.filter((entry) => showParent || !entry.isParentArea),
    [orderedRows, showParent]
  );

  const resolveRangesWithFallback = (metric: any, row: any, area: any) => {
    const areaTypeId = area?.areaType?.id ?? area?.areaTypeId ?? null;
    const selectedAreaTypeDefaults = areaTypeId
      ? metric?.areaTypeRuleDefaults?.[String(areaTypeId)]
      : undefined;
    const direct = resolveBenchmarkRangesForMetric(metric, area);
    if (direct && Object.keys(direct).length > 0) {
      logBenchmarkCardsDebug({
        stage: 'direct-hit',
        rowMetricKey: row?.metricKey,
        metricKey: metric?.key,
        metricName: metric?.name,
        areaId: area?.id,
        areaTypeId,
        metricAreaTypeRuleDefaultKeys: Object.keys(metric?.areaTypeRuleDefaults || {}),
        selectedAreaTypeDefaults,
        metricBenchmarkRangesRaw: metric?.benchmarkRange ?? metric?.benchmarkRanges ?? null,
        directRanges: direct,
      });
      return { ranges: direct, sourceMetric: metric };
    }

    const candidates = findBenchmarkMetricCandidates(metricCandidateIndex, [
      row?.metricKey,
      metric?.key,
      metric?.sourceKey,
      metric?.name,
      extractBaseMetricKey(row?.metricKey),
      extractBaseMetricKey(metric?.key),
    ]);
    const candidateDiagnostics = candidates.map((candidate: any) => {
      const candidateRanges = resolveBenchmarkRangesForMetric(candidate, area);
      return {
        key: candidate?.key,
        name: candidate?.name,
        hasRanges: !!candidateRanges && Object.keys(candidateRanges).length > 0,
        rangeKeys: candidateRanges ? Object.keys(candidateRanges) : [],
      };
    });
    logBenchmarkCardsDebug({
      stage: 'fallback-search',
      rowMetricKey: row?.metricKey,
      metricKey: metric?.key,
      metricName: metric?.name,
      areaId: area?.id,
      areaTypeId,
      metricAreaTypeRuleDefaultKeys: Object.keys(metric?.areaTypeRuleDefaults || {}),
      selectedAreaTypeDefaults,
      metricBenchmarkRangesRaw: metric?.benchmarkRange ?? metric?.benchmarkRanges ?? null,
      directRanges: direct,
      candidateKeys: candidates.map((candidate: any) => candidate?.key),
      candidateDiagnostics,
    });

    for (const candidate of candidates) {
      const ranges = resolveBenchmarkRangesForMetric(candidate, area);
      if (ranges && Object.keys(ranges).length > 0) {
        logBenchmarkCardsDebug({
          stage: 'fallback-hit',
          rowMetricKey: row?.metricKey,
          selectedMetricKey: candidate?.key,
          selectedMetricName: candidate?.name,
          areaId: area?.id,
          areaTypeId,
          selectedMetricAreaTypeRuleDefaultKeys: Object.keys(candidate?.areaTypeRuleDefaults || {}),
          selectedMetricAreaTypeDefaults: areaTypeId
            ? candidate?.areaTypeRuleDefaults?.[String(areaTypeId)]
            : undefined,
          selectedMetricBenchmarkRangesRaw:
            candidate?.benchmarkRange ?? candidate?.benchmarkRanges ?? null,
          ranges,
        });
        return { ranges, sourceMetric: candidate };
      }
    }

    logBenchmarkCardsDebug({
      stage: 'no-ranges',
      rowMetricKey: row?.metricKey,
      metricKey: metric?.key,
      metricName: metric?.name,
      areaId: area?.id,
      areaTypeId,
      metricAreaTypeRuleDefaultKeys: Object.keys(metric?.areaTypeRuleDefaults || {}),
      selectedAreaTypeDefaults,
      metricBenchmarkRangesRaw: metric?.benchmarkRange ?? metric?.benchmarkRanges ?? null,
      directRanges: direct,
      candidateKeys: candidates.map((candidate: any) => candidate?.key),
      candidateDiagnostics,
      availableMetricsWithRanges: (Array.isArray(metrics) ? metrics : [])
        .map((entry: any) => {
          const entryRanges = resolveBenchmarkRangesForMetric(entry, area);
          return {
            key: entry?.key,
            name: entry?.name,
            hasRanges: !!entryRanges && Object.keys(entryRanges).length > 0,
            rangeKeys: entryRanges ? Object.keys(entryRanges) : [],
          };
        })
        .filter((entry) => entry.hasRanges)
        .slice(0, 50),
    });
    return { ranges: undefined, sourceMetric: null };
  };

  if (visibleRows.length === 0) {
    return <Typography.Text type="secondary">No data</Typography.Text>;
  }

  return (
    <div className="report-benchmark-workspace">
      <div className="report-benchmark-head">
        <div className="report-benchmark-title">{section?.title || 'Benchmark'}</div>
        <div className="report-benchmark-meta">
          {[selectedAreaName, dateRangeLabel].filter(Boolean).join(' · ')}
        </div>
      </div>

      <Row gutter={[12, 12]}>
        {visibleRows.map((entry) => {
        const { row, originalIndex, metricKey, areaId, area, areaName, isParentArea } = entry;
        const metric = metricByKey.get(metricKey);
        const metricName = metric?.name || metricKey;
        const { ranges: benchmarkRanges, sourceMetric } = resolveRangesWithFallback(metric, row, area);
        const palette = Array.isArray(metric?.colorPalette) && metric.colorPalette.length > 0
          ? metric.colorPalette
          : DEFAULT_PALETTE;
        const accentColor = palette.find((color: string) => Boolean(color)) || DEFAULT_PALETTE[0];
        const fieldCount = activeFields.length || 1;
        const wideSpan = fieldCount <= 4 ? Math.floor(24 / fieldCount) : 6;
        const columnProps = fieldCount <= 4
          ? { xs: fieldCount === 1 ? 24 : 12, sm: wideSpan, md: wideSpan, lg: wideSpan }
          : { xs: 12, sm: 8, md: 6, lg: 4 };

        return (
          <Col key={`${metricKey}-${areaId}-${originalIndex}`} {...cardColumnProps}>
            <div
              style={{
                padding: 16,
                borderRadius: 12,
                backgroundColor: toRgba(accentColor, isParentArea ? 0.2 : 0.09),
                border: `${isParentArea ? 2 : 1}px solid ${toRgba(accentColor, isParentArea ? 0.72 : 0.2)}`,
                boxShadow: isParentArea ? `0 3px 12px ${toRgba(accentColor, 0.28)}` : 'none',
              }}
            >
              <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
                <Col>
                  {onMetricClick ? (
                    <MetricLink
                      metricKey={metricKey}
                      metricName={metricName}
                      metricId={metric?.id}
                      areaId={areaId}
                      onClick={onMetricClick}
                    />
                  ) : (
                    <Typography.Text strong>{metricName}</Typography.Text>
                  )}
                </Col>
                <Col>
                  <Space size={6}>
                    {areaName ? (
                      onAreaClick ? (
                        <AreaLink
                          areaId={areaId}
                          areaName={isParentArea ? `Parent area: ${areaName}` : areaName}
                          onClick={onAreaClick}
                          style={{ fontSize: 12 }}
                        />
                      ) : (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {isParentArea ? `Parent area: ${areaName}` : areaName}
                        </Typography.Text>
                      )
                    ) : null}
                    {isParentArea ? (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '3px 10px',
                          borderRadius: 999,
                          border: `1px solid ${toRgba(accentColor, 0.8)}`,
                          backgroundColor: toRgba(accentColor, 0.2),
                          fontSize: 12,
                          fontWeight: 700,
                          color: '#1f1f1f',
                          letterSpacing: 0.2,
                        }}
                      >
                        Current Parent Area
                      </span>
                    ) : null}
                  </Space>
                </Col>
              </Row>
              <Row gutter={[12, 12]}>
                {activeFields.map((field: BenchmarkField) => {
                const numericValue = getBenchmarkFieldValue(row, field);
                const match = resolveBenchmarkBand(numericValue, benchmarkRanges);
                logBenchmarkCardsDebug({
                  stage: 'value-band-check',
                  metricKey,
                  metricName,
                  areaId,
                  areaTypeId: area?.areaType?.id ?? area?.areaTypeId ?? null,
                  areaTypeName: area?.areaType?.name ?? null,
                  metricAreaTypeRuleDefaultKeys: Object.keys(metric?.areaTypeRuleDefaults || {}),
                  field,
                  numericValue,
                  benchmarkRanges,
                  match,
                  sourceMetricKey: sourceMetric?.key ?? null,
                });
                const tagColor = match ? BAND_COLORS[match.band] || '#8c8c8c' : '#8c8c8c';
                const needleBand = match ? Number(match.band) : null;
                const benchmarkLabel = match
                  ? match.inRange
                    ? match.label
                    : `${match.label} (closest)`
                  : 'No benchmark configured';
                const benchmarkDescription = match
                  ? BENCHMARK_DESCRIPTIONS[String(match.band)] || 'Benchmark description unavailable.'
                  : 'Define benchmark ranges to classify this metric.';
                const formattedValue = formatBenchmarkValue(numericValue, row?.unit);
                const displayNumber = (typeof numericValue === 'number' && Number.isFinite(numericValue))
                  ? Number(numericValue).toFixed(2).replace(/\.?0+$/, '')
                  : '—';
                const unitLabel = row?.unit || metric?.unit || '';
                const sourceLabel =
                  sourceMetric && sourceMetric !== metric
                    ? ` (using ${sourceMetric?.name || sourceMetric?.key})`
                    : '';
                const details = match?.range
                  ? `Band ${match.band}: ${match.range.min ?? '—'} to ${match.range.max ?? '—'}${sourceLabel}. Value: ${formattedValue}`
                  : `No benchmark ranges configured for ${metricName}. Value: ${formattedValue}`;

                  return (
                    <Col key={`${field}-${metricKey}-${areaId}`} {...columnProps}>
                      <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                        {BENCHMARK_FIELD_LABELS[field]}
                      </Typography.Text>
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <GaugeDial needleBand={needleBand} />
                      </div>
                      <div style={{ textAlign: 'center', marginTop: -56 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
                          <Typography.Text
                            strong
                            style={{ color: '#252525ff', fontSize: 30, lineHeight: 1 }}
                          >
                            {displayNumber}
                          </Typography.Text>
                          {unitLabel ? (
                            <Typography.Text
                              style={{ color: '#4a4a4a', fontSize: 14, fontWeight: 600, lineHeight: 1 }}
                            >
                              {unitLabel}
                            </Typography.Text>
                          ) : null}
                        </span>
                      </div>
                      <Tooltip title={details}>
                        <div style={{ textAlign: 'center', marginTop: 4, cursor: 'help' }}>
                          <Typography.Text strong style={{ color: tagColor }}>
                            {benchmarkLabel}
                          </Typography.Text>
                          <div>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                              {benchmarkDescription}
                            </Typography.Text>
                          </div>
                        </div>
                      </Tooltip>
                    </Col>
                  );
                })}
              </Row>
            </div>
          </Col>
        );
      })}
      </Row>
    </div>
  );
};

export default ReportBenchmarkCards;
